import React, { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import styled, { keyframes } from "styled-components";
import Reveal from "../v2/Reveal";
import ParticleSculpture from "../BusinessDivision/ParticleSculpture";
import {
	SectionV2,
	InnerV2,
	HeadV2,
	GlassCard,
	accents,
} from "../v2/primitives";

const DIFFERENTIALS = [
	{
		id: "brilliantPeople",
		icon: "/landings/assets/icons/certificate.svg",
		textKey: "text-1",
		accent: accents.green,
	},
	{
		id: "ecosystem",
		icon: "/landings/assets/icons/eco.svg",
		textKey: "text-2",
		accent: accents.cyan,
	},
	{
		id: "commitmentToDelivery",
		icon: "/landings/assets/icons/hand-shake.svg",
		textKey: "text-3",
		accent: accents.violet,
	},
];

const FRAMEWORKS = [
	{
		id: "squadIa",
		sculpture: "neural",
		textKey: "text-4",
		accent: accents.lime,
		href: "/produtos/squad-ia",
		// the graph is meant to be see-through (no backCull): hiding the far
		// side would erase half the edges. Fine sparse grain: few pixel-thin
		// dots, spaced out so nodes and links never clump into blobs
		count: 850,
		pointScale: 0.26,
	},
	{
		id: "rpaAutomation",
		sculpture: "robot",
		textKey: "text-5",
		accent: accents.green,
		href: "/produtos/rpa",
		// simplified: fewer points for a cleaner, less busy robot
		count: 1550,
	},
];

/** glass card that tilts toward the cursor and carries a moving spotlight */
function TiltCard({ accent, index, children, onClick }) {
	const ref = useRef(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reducedMotion) return undefined;

		let rafId = null;
		let px = 0.5;
		let py = 0.5;

		const apply = () => {
			rafId = null;
			el.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
			el.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
			el.style.transform = `perspective(700px) rotateX(${((0.5 - py) * 5).toFixed(2)}deg) rotateY(${((px - 0.5) * 7).toFixed(2)}deg) translateY(-4px)`;
		};
		const onMove = (e) => {
			const r = el.getBoundingClientRect();
			px = (e.clientX - r.left) / r.width;
			py = (e.clientY - r.top) / r.height;
			if (rafId === null) rafId = requestAnimationFrame(apply);
		};
		const onLeave = () => {
			el.style.transform = "";
		};

		el.addEventListener("pointermove", onMove, { passive: true });
		el.addEventListener("pointerleave", onLeave);
		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			el.removeEventListener("pointermove", onMove);
			el.removeEventListener("pointerleave", onLeave);
		};
	}, []);

	return (
		<DnaCard
			ref={ref}
			$accent={accent}
			$i={index}
			$clickable={!!onClick}
			onClick={onClick}
			role={onClick ? "link" : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={
				onClick
					? (e) => {
							if (e.key === "Enter") onClick();
						}
					: undefined
			}
		>
			{children}
		</DnaCard>
	);
}

const VerzelDna = () => {
	const { t } = useTranslation();
	const router = useRouter();
	const title = t("ourDifferentials.title");
	const titleWords = title.split(" ");
	const lastWord = titleWords.pop();

	return (
		<SectionV2 $accent={accents.green} $glowAt="18% 0%">
			<InnerV2>
				<Reveal>
					<HeadV2 $accent={accents.green}>
						<span className="overline">DNA Verzel</span>
						<h2>
							{titleWords.join(" ")} <em>{lastWord}</em>
						</h2>
					</HeadV2>
				</Reveal>

				<CardsGrid>
					{DIFFERENTIALS.map((card, index) => (
						<Reveal key={card.id} delay={index * 130}>
							<TiltCard accent={card.accent} index={index}>
								<div className="tile-wrap">
									<span className="orbit" aria-hidden="true">
										<i />
									</span>
									<div className="icon-tile">
										<img src={card.icon} alt="" loading="lazy" />
									</div>
								</div>
								<h3>{t(`ourDifferentials.${card.id}`)}</h3>
								<p className="lead">
									{t(`ourDifferentials.lead-${index + 1}`)}
								</p>
								<p>{t(`ourDifferentials.${card.textKey}`)}</p>
							</TiltCard>
						</Reveal>
					))}
				</CardsGrid>

				<Reveal>
					<FrameworksBox $accent={accents.lime}>
						<div className="frame-title">
							<i aria-hidden="true" />
							<h3>{t("ourDifferentials.frameworksTitle")}</h3>
							<i aria-hidden="true" />
						</div>
						<div className="frame-body">
							<FrameworksGrid>
							{FRAMEWORKS.map((card, index) => (
						<Reveal key={card.id} delay={index * 130}>
							<TiltCard
								accent={card.accent}
								index={index + 3}
								onClick={() =>
									router.push(`/${router.query.locale}${card.href}`)
								}
							>
								<div className="tile-wrap">
									<span className="orbit" aria-hidden="true">
										<i />
									</span>
									<div className="sculpt-tile">
										<ParticleSculpture
											shape={card.sculpture}
											accent={card.accent}
											count={card.count || 2600}
											pointScale={card.pointScale || 0.5}
											backCull={card.backCull}
										/>
									</div>
								</div>
								<h3>{t(`ourDifferentials.${card.id}`)}</h3>
								<p className="lead">
									{t(`ourDifferentials.lead-${index + 4}`)}
								</p>
								<p>{t(`ourDifferentials.${card.textKey}`)}</p>
								<span className="card-cta">
									{t("businessDivision.knowMore")}
									<svg
										width="14"
										height="14"
										viewBox="0 0 16 16"
										fill="none"
										aria-hidden="true"
									>
										<path
											d="M3 8h10M9 4l4 4-4 4"
											stroke="currentColor"
											strokeWidth="1.6"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</span>
							</TiltCard>
						</Reveal>
								))}
							</FrameworksGrid>
						</div>
					</FrameworksBox>
				</Reveal>
			</InnerV2>
		</SectionV2>
	);
};

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
  }
`;

/* the proprietary-frameworks pair, boxed inside the group frame */
const FrameworksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }
`;

/* hand-built legend frame: the title row IS the top edge of the box. Two
   flex segments leave the title's sides at its vertical center, run to the
   container edges, curve down and hand off to the body's side borders — so
   the frame visibly grows out of the title and wraps the cards below it.
   (a native fieldset/legend notch proved unreliable across this stack) */
const FrameworksBox = styled.div`
  position: relative;
  margin: clamp(36px, 4.5vw, 56px) 0 0 0;

  .frame-title {
    display: flex;
    align-items: stretch;
    height: 34px;

    /* the lines out of the title's sides: horizontal at its center (11px),
       rounding down at the corners into the body's side borders. The half
       unit of stroke lets the browser anti-alias the curves smoothly */
    i {
      flex: 1;
      margin-top: 11px;
      border-top: 1.5px solid ${(p) => p.$accent + "99"};
    }

    i:first-child {
      border-left: 1.5px solid ${(p) => p.$accent + "99"};
      border-top-left-radius: 22px;
    }

    i:last-child {
      border-right: 1.5px solid ${(p) => p.$accent + "99"};
      border-top-right-radius: 22px;
    }

    h3 {
      align-self: flex-start;
      margin: 0;
      padding: 0 16px;
      line-height: 22px;
      font-family: "Hind Madurai";
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 3.5px;
      text-transform: uppercase;
      text-align: center;
      text-decoration: none;
      white-space: nowrap;
      color: ${(p) => p.$accent};
    }
  }

  .frame-body {
    border: 1.5px solid ${(p) => p.$accent + "99"};
    border-top: none;
    border-radius: 0 0 22px 22px;
    padding: 10px 16px 20px 16px;

    @media (min-width: 992px) {
      padding: 16px 28px 28px 28px;
    }
  }
`;

const tileFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
`;

const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const DnaCard = styled(GlassCard)`
  --mx: 50%;
  --my: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 44px 28px 36px 28px;
  overflow: hidden;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  /* explicit route into the product landing */
  .card-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 22px;
    font-family: "Hind Madurai";
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: ${(p) => p.$accent};
    border-bottom: 1px solid ${(p) => p.$accent + "59"};
    padding-bottom: 4px;
    transition: border-color 0.25s ease, gap 0.25s ease;

    svg,
    svg * {
      color: inherit;
    }
  }

  &:hover .card-cta {
    border-color: ${(p) => p.$accent};
    gap: 12px;
  }

  /* cursor spotlight sweeping the glass */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: radial-gradient(
      340px circle at var(--mx) var(--my),
      ${(p) => p.$accent + "17"},
      transparent 65%
    );
    opacity: 0;
    transition: opacity 0.35s ease;
  }

  &:hover::after {
    opacity: 1;
  }

  .tile-wrap {
    position: relative;
    margin-bottom: 28px;
    animation: ${tileFloat} ${(p) => 4.6 + p.$i * 0.7}s ease-in-out
      ${(p) => -p.$i * 1.3}s infinite;
  }

  /* a spark of energy orbiting the icon, echoing the sections above */
  .orbit {
    position: absolute;
    inset: -13px;
    border-radius: 26px;
    border: 1px dashed ${(p) => p.$accent + "2b"};
    animation: ${orbitSpin} ${(p) => 8 + p.$i * 1.5}s linear infinite;
    pointer-events: none;

    i {
      position: absolute;
      top: -3.5px;
      left: 50%;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${(p) => p.$accent};
      box-shadow: 0 0 12px ${(p) => p.$accent + "cc"};
    }
  }

  &:hover .orbit {
    animation-duration: 2.6s;
  }

  /* particle sculptures live free, no tile background boxing them in */
  .sculpt-tile {
    position: relative;
    width: 118px;
    height: 118px;
  }

  .icon-tile {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 92px;
    height: 92px;
    border-radius: 20px;
    background: linear-gradient(
      160deg,
      ${(p) => p.$accent + "24"} 0%,
      rgba(255, 255, 255, 0.03) 100%
    );
    border: 1px solid ${(p) => p.$accent + "40"};
    box-shadow: 0 12px 34px -14px ${(p) => p.$accent + "66"};

    img {
      width: 48px;
      height: 48px;
    }
  }

  h3 {
    font-family: "Hind Madurai";
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 14px 0;
    transition: color 0.3s ease, text-shadow 0.3s ease;
  }

  &:hover h3 {
    color: ${(p) => p.$accent};
    text-shadow: 0 0 26px ${(p) => p.$accent + "59"};
  }

  /* the value statement leads, the how supports */
  .lead {
    font-family: "Hind Madurai";
    font-size: 1.06rem;
    font-weight: 600;
    line-height: 1.5;
    color: #e8ebef;
    margin: 0 0 10px 0;
    text-wrap: balance;
  }

  /* the body copy owns the card's remaining height and centers in it, so
     shorter texts sit balanced instead of leaving a void at the bottom */
  p:not(.lead) {
    flex: 1;
    display: flex;
    align-items: center;
    font-family: "Hind Madurai";
    font-size: 0.92rem;
    line-height: 1.7;
    color: #98a0ab;
    margin: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .tile-wrap,
    .orbit {
      animation: none;
    }
  }
`;

export default VerzelDna;
